import { useReducer } from "react";
import _ from "lodash";

import {
  isFirmwareTypeId,
  isPatternTypeId,
  isImageTypeId,
  isDocumentId,
  getCompTypeCategory,
} from "utils/compType";
import { useFetchWithReducer } from "hooks";

const SET_DATA = "set_data";
const SET_LOADING_STATUS = "set_loading_status";
const SELECT_COMP_FILTER = "select_comp_filter";
const RESET_COMP_FILTER = "reset_comp_filter";

const defaultCompCategoryList = { fm: [], ptn: [], vmImg: [], document: [] };
const defaultSelectedFilters = {
  productLine: "all",
  productSku: "all",
  compTypeCategory: "all",
};
const initialState = {
  compList: [],
  compCategoryList: _.cloneDeep(defaultCompCategoryList),
  compFilters: {
    productLineList: [],
    productSkuList: [],
    componentTypeList: [],
  },
  loading: false,
  selectedFilters: { ...defaultSelectedFilters },
};

function reducer(state, action) {
  switch (action.type) {
    case SET_LOADING_STATUS: {
      return { ...state, loading: action.payload.status };
    }
    case SET_DATA: {
      const {
        data: { compList: data },
      } = action.payload;
      const compList = data.map((compInfo) => {
        // init comp list
        const compType = _.get(compInfo, "compType");
        if (isFirmwareTypeId(compType)) {
          compInfo.compTypeCategory = "Firmware";
        } else if (isPatternTypeId(compType)) {
          const compId = _.get(compInfo, "compId");
          const compTypeCategory = getCompTypeCategory(compId);
          compInfo.compTypeCategory = compTypeCategory;
        } else if (isImageTypeId(compType)) {
          compInfo.compTypeCategory = "VM Image";
        } else if (isDocumentId(compType)) {
          compInfo.compTypeCategory = "Document";
        }
        return compInfo;
      });

      // group comp
      const compCategoryList = compList.reduce((result, compInfo) => {
        if (isFirmwareTypeId(compInfo.compType)) {
          result.fm.push(compInfo);
        } else if (isPatternTypeId(compInfo.compType)) {
          result.ptn.push(compInfo);
        } else if (isImageTypeId(compInfo.compType)) {
          result.vmImg.push(compInfo);
        } else if (isDocumentId(compInfo.compType)) {
          result.document.push(compInfo);
        }
        return result;
      }, _.cloneDeep(defaultCompCategoryList));

      return {
        ...state,
        compList,
        compCategoryList,
        compFilters: getComponentFilters(compList, state.compFilters),
      };
    }
    case SELECT_COMP_FILTER: {
      const { key, value } = action.payload;
      const { compList } = state;
      const newSelectedFilters = {
        ...state.selectedFilters,
        [key]: value,
      };

      // generate comp filters based on selected items
      const newCompFilters = getComponentFilters(compList, newSelectedFilters);

      // set selected filter to empty string if there is no the same item in filters
      const { productLineList, productSkuList, componentTypeList } =
        newCompFilters;
      if (
        !productLineList.find((name) => name === newSelectedFilters.productLine)
      ) {
        newSelectedFilters.productLine = "all";
      }
      if (
        !productSkuList.find((categoryInfo) => {
          return categoryInfo.items.find(
            (name) => name === newSelectedFilters.productSku
          );
        })
      ) {
        newSelectedFilters.productSku = "all";
      }
      if (
        !componentTypeList.find(
          (name) => name === newSelectedFilters.compTypeCategory
        )
      ) {
        newSelectedFilters.compTypeCategory = "all";
      }

      // generate new comp category list
      const newCompCategoryList = getCompCategoryList(
        compList,
        newSelectedFilters
      );

      return {
        ...state,
        selectedFilters: newSelectedFilters,
        compCategoryList: newCompCategoryList,
        compFilters: newCompFilters,
      };
    }
    case RESET_COMP_FILTER: {
      const newSelectedFilters = { ...defaultSelectedFilters };
      const { compList } = state;
      const newCompCategoryList = getCompCategoryList(
        compList,
        newSelectedFilters
      );
      return {
        ...state,
        selectedFilters: newSelectedFilters,
        compCategoryList: newCompCategoryList,
        compFilters: getComponentFilters(compList, newSelectedFilters),
      };
    }
    default:
      return state;
  }
}

const getCompCategoryList = (compList, filters) => {
  const selectedProductLine =
    filters.productLine === "all" ? "" : filters.productLine;
  const selectedproductSku =
    filters.productSku === "all" ? "" : filters.productSku;
  const selectedCompCategoryType =
    filters.compTypeCategory === "all" ? "" : filters.compTypeCategory;

  return compList.reduce((result, compInfo) => {
    // filter by comp type category
    let isCompTypeCategoryMatched = false;
    const compTypeCategory = _.get(compInfo, "compTypeCategory");
    if (
      !selectedCompCategoryType ||
      (selectedCompCategoryType &&
        compTypeCategory === selectedCompCategoryType)
    ) {
      isCompTypeCategoryMatched = true;
    }

    // filter by product line and product SKU
    const isproductSkuMatched = !!_.get(compInfo, "productSkuList", []).find(
      (skuInfo) => {
        let isProductLineMatched = false;
        let isProductLineSkuMatched = false;
        if (
          !selectedProductLine ||
          (selectedProductLine && skuInfo.productLine === selectedProductLine)
        ) {
          isProductLineMatched = true;
        }
        if (
          !selectedproductSku ||
          (selectedproductSku && skuInfo.productSku === selectedproductSku)
        ) {
          isProductLineSkuMatched = true;
        }
        return (
          isProductLineMatched &&
          isProductLineSkuMatched &&
          isCompTypeCategoryMatched
        );
      }
    );

    if (isproductSkuMatched) {
      // put comp into category list
      if (isFirmwareTypeId(compInfo.compType)) {
        result.fm.push(compInfo);
      } else if (isPatternTypeId(compInfo.compType)) {
        result.ptn.push(compInfo);
      } else if (isImageTypeId(compInfo.compType)) {
        result.vmImg.push(compInfo);
      } else if (isDocumentId(compInfo.compType)) {
        result.document.push(compInfo);
      }
    }
    return result;
  }, _.cloneDeep(defaultCompCategoryList));
};

const getComponentFilters = (compList, filters) => {
  const productLineList = [];
  const productSkuList = [];
  const componentTypeList = [];
  const selectedProductLine =
    filters.productLine === "all" ? "" : filters.productLine;
  const selectedProductSku =
    filters.productSku === "all" ? "" : filters.productSku;
  compList.forEach((compInfo) => {
    _.get(compInfo, "productSkuList", []).forEach((skuInfo) => {
      // product line
      const productLine = _.get(skuInfo, "productLine");
      if (!productLineList.includes(productLine)) {
        productLineList.push(productLine);
      }

      // product sku and comp type category
      if (selectedProductLine) {
        if (productLine === selectedProductLine) {
          addProductSkuFilter(skuInfo, productSkuList);

          if (selectedProductSku) {
            const productSku = _.get(skuInfo, "productSku");
            if (selectedProductSku === productSku) {
              addCompTypeCategoryFilter(compInfo, componentTypeList);
            }
          } else {
            addCompTypeCategoryFilter(compInfo, componentTypeList);
          }
        }
      } else {
        addProductSkuFilter(skuInfo, productSkuList);

        if (selectedProductSku) {
          const productSku = _.get(skuInfo, "productSku");
          if (selectedProductSku === productSku) {
            addCompTypeCategoryFilter(compInfo, componentTypeList);
          }
        } else {
          addCompTypeCategoryFilter(compInfo, componentTypeList);
        }
      }
    });
  });
  return { productLineList, productSkuList, componentTypeList };
};

const addProductSkuFilter = (skuInfo, productSkuList) => {
  const productSkuCategory = _.get(skuInfo, "productCategory");
  const productSku = _.get(skuInfo, "productSku");
  const productSkuItem = productSkuList.find(
    (skuInfo) => skuInfo.name === productSkuCategory
  );

  if (productSkuItem) {
    if (!productSkuItem.items.includes(productSku)) {
      productSkuItem.items.push(productSku);
    }
  } else {
    const skuInfo = {
      name: productSkuCategory,
      items: [productSku],
    };
    productSkuList.push(skuInfo);
  }
};

const addCompTypeCategoryFilter = (compInfo, componentTypeList) => {
  const compTypeCategory = _.get(compInfo, "compTypeCategory");
  if (!componentTypeList.includes(compTypeCategory)) {
    componentTypeList.push(compTypeCategory);
  }
};

const dispatchSelectCompFilter = (dispatcher, key, value) => {
  dispatcher({ type: SELECT_COMP_FILTER, payload: { key, value } });
};

const dispatchResetCompFilter = (dispatcher) => {
  dispatcher({ type: RESET_COMP_FILTER });
};

export const useCompList = (compType) => {
  const [state, dispatcher] = useReducer(reducer, {
    ...initialState,
  });
  useFetchWithReducer(dispatcher, `/api/v1/file/${compType}`, "get");
  return {
    state,
    compList: state.compList,
    compCategoryList: state.compCategoryList,
    compFilters: state.compFilters,
    loading: state.loading,
    selectCompFilter: dispatchSelectCompFilter.bind(this, dispatcher),
    selectedFilters: state.selectedFilters,
    resetFilters: dispatchResetCompFilter.bind(this, dispatcher),
  };
};
